import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@platform/cj-platform-navigation';
import {
  CJField,
  CJForm,
  CJFormBlock,
  useCJForm,
} from '@cjdev-internal/visual-stack-x/CJForm';
import type { CJFormProps } from '@cjdev-internal/visual-stack-x/CJForm';
import { Button } from '@cjdev-internal/visual-stack-x/Button';
import { Row } from '@cjdev-internal/visual-stack-x/components/Row';
import intl from 'utils/intl';
import { useToast } from '@cjdev-internal/visual-stack-x/Toast';
import { managePartnerships } from './managePartnerships';
import { PartnerRow } from './components/PartnerRow';
import {
  disabledProdPartnerNames,
  DraftPartnerRow,
  keyField,
  partnerField,
  PartnershipPageProps,
  statusField,
} from './partnershipPageTypes';
import styled from 'styled-components';
import { LoadingAnimation } from '@cjdev-internal/visual-stack-x/LoadingAnimation';
import { appConfig } from 'appConfig';
import { content } from './message';
import {
  Partner,
  PartnerName,
} from '@callback-cats/partnerships-ts/Partnership';

const StyledLoadingAnimation = styled(LoadingAnimation)`
  margin: 20px auto;
  display: block;
  width: 100%;
  height: 50px;
`;

function generateDefaults(partnerships: Partner[]): Record<string, string> {
  const defaultValuesArray = partnerships?.map((partnership, index) => {
    const data: Record<string, string> = {
      [partnerField + index.toString()]: partnership.name,
      [keyField + index.toString()]: partnership.data.key,
      [statusField + index.toString()]: partnership.mode,
    };

    // Dynamically add all additional data fields beyond 'key'
    Object.entries(partnership.data).forEach(([fieldName, fieldValue]) => {
      if (fieldName !== 'key' && typeof fieldValue === 'string') {
        data[fieldName + index] = fieldValue;
      }
    });

    return data;
  });
  return Object.assign({}, ...(defaultValuesArray || []));
}

function initializePartnerRows(partnerships: Partner[]): DraftPartnerRow[] {
  return Array.from(
    partnerships,
    (value, index): DraftPartnerRow => ({
      rowKey: index.toString(),
      partnership: value,
    })
  );
}

export const PartnershipPage = (props: PartnershipPageProps) => {
  const [toastMount, showToast] = useToast(true);
  const { api, user } = props;
  const cid = user.currentCompany.id.toString();
  const navigate = useNavigate();
  const { tagId: tagIdFromPath } = useParams<{ tagId: string }>();
  const tagIdFromParent = props.existingPartnerships.tagId;
  const partnerCount = Object.keys(PartnerName).length;
  const tagId = tagIdFromParent || tagIdFromPath || 'tagIdNotFound';
  const [existingPartnerships, setExistingPartnerships] = useState<Partner[]>(
    (props.existingPartnerships.partnerships || []).map((p) => p.value)
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const defaultValues = useMemo(
    () => generateDefaults(existingPartnerships),
    [existingPartnerships]
  );
  const config = useCJForm({
    defaultValues,
  });

  // State for draft (new) partner rows
  const [draftPartnerRows, setDraftPartnerRows] = useState<DraftPartnerRow[]>(
    []
  );

  // Compute partnerRows: always show existingPartnerships (from API or props) plus draft rows, but filter out duplicates by partner name
  const partnerRows = useMemo(() => {
    const allRows = [
      ...initializePartnerRows(existingPartnerships),
      ...draftPartnerRows,
    ];
    const seen = new Set();
    return allRows.filter((row) => {
      // Use partner name as unique identifier if available
      const name = row.partnership?.name || row.rowKey;
      if (seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  }, [existingPartnerships, draftPartnerRows]);

  // Track if we have set defaultValues after API fetch
  const [apiDefaultsHaveBeenSet, setApiDefaultsHaveBeenSet] = useState(false);

  useEffect(() => {
    if (
      !tagIdFromParent &&
      tagIdFromPath &&
      existingPartnerships.length === 0
    ) {
      setIsLoading(true);
      props.api
        .fetchPartnership(cid, { id: tagId })
        .then((newPartnerships) => {
          setExistingPartnerships(newPartnerships.map((p) => p.value));
          setApiDefaultsHaveBeenSet(false); // allow reset after fetch
        })
        .catch((e) =>
          showToast({ message: (e as Error).message, type: 'warning' })
        )
        .finally(() => setIsLoading(false));
    }
  }, [
    tagIdFromParent,
    tagIdFromPath,
    existingPartnerships.length,
    cid,
    tagId,
    props.api,
    showToast,
  ]);

  useEffect(() => {
    if (!apiDefaultsHaveBeenSet && existingPartnerships.length > 0) {
      config.reset(generateDefaults(existingPartnerships));
      setApiDefaultsHaveBeenSet(true);
    }
  }, [existingPartnerships, config, apiDefaultsHaveBeenSet]);

  // Add Partner handler: adds a new draft row
  const handleAddPartner = () => {
    setDraftPartnerRows((prev) => [
      ...prev,
      { rowKey: partnerRows.length.toString(), partnership: undefined },
    ]);
  };

  const partnerRowCount = partnerRows.length;

  const renderEmptyState = () => (
    <CJField columnSpan={'full'}>
      {intl.formatMessage(content.noPartners)}
    </CJField>
  );

  const renderPartnerRows = () =>
    partnerRows.map((row) => (
      <PartnerRow
        key={row.rowKey}
        rowId={row.rowKey}
        existingRows={existingPartnerships}
        draftPartnerRows={partnerRows}
        setPartnerRows={setDraftPartnerRows}
        config={config}
      />
    ));

  const renderAddPartnerButton = () => (
    <CJField align="start">
      <Button
        type="link"
        onClick={handleAddPartner}
        disabled={
          partnerRowCount ===
          (appConfig.environment !== 'production'
            ? partnerCount
            : partnerCount - disabledProdPartnerNames.length)
        }
      >
        {'+ Add Partner'}
      </Button>
    </CJField>
  );

  const renderActionButtons = () => (
    <Row justify="end">
      <Button
        htmlType="button"
        type="tertiary"
        onClick={() => {
          navigate('/list');
        }}
      >
        CANCEL
      </Button>
      <Button
        htmlType="submit"
        type="primary"
        disabled={!config.formState.isDirty}
      >
        SAVE
      </Button>
    </Row>
  );

  const renderFormContent = () => {
    // Early exit for special case
    if (partnerRowCount === 0 && tagIdFromPath && isLoading) {
      return null;
    }

    return (
      <>
        {partnerRowCount === 0 && renderEmptyState()}
        {renderPartnerRows()}
        {renderAddPartnerButton()}
        {renderActionButtons()}
      </>
    );
  };

  return (
    <>
      {toastMount}
      <CJForm
        // TODO: remove cast once @cjdev-internal/visual-stack-x updates useCJForm return type to include setValues/resetDefaultValues/subscribe from react-hook-form UseFormReturn
        {...(config as unknown as CJFormProps)}
        heading={`Tag ID - ${tagId}`}
        showRequiredIndicator={true}
        formPageLayoutMode
        columnCount={1}
        id="PartnershipForm"
        onSubmit={async (data) => {
          try {
            await managePartnerships(
              api,
              existingPartnerships,
              tagId,
              cid,
              data,
              partnerRowCount,
              navigate,
              showToast
            );
          } catch (e) {
            const failedMessage = intl.formatMessage(
              content.partnershipSaveFailAlert
            );
            showToast({ message: failedMessage, type: 'warning' });
          }
        }}
      >
        {isLoading ? (
          <StyledLoadingAnimation>
            <LoadingAnimation
              loadingMessage={intl.formatMessage(content.partnershipLoading)}
            />
          </StyledLoadingAnimation>
        ) : (
          <></>
        )}
        <CJFormBlock>{renderFormContent()}</CJFormBlock>
      </CJForm>
    </>
  );
};
